import {
  DEFAULT_CONFIG,
  type GamePhase,
  type PlayerColor,
} from "@cloudnest/yinsh-engine";
import { useEffect, useRef, useState } from "react";
import { buildLobbyPath, navigateTo, useRoute } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { downloadJson } from "../../shared/utils/download-json";
import {
  getParticipantDisplayName,
  getParticipantDisplayNames,
} from "../../shared/utils/participants";
import { Board } from "./components/Board";
import { GameInfo } from "./components/GameInfo";
import { GameOverModal } from "./components/GameOverModal";
import { HintCard } from "./components/HintCard";
import { ReplayStepper } from "./components/ReplayStepper";
import { RowPickerSheet } from "./components/RowPickerSheet";
import { TopBar } from "./components/TopBar";
import { TurnBanner } from "./components/TurnBanner";
import { buildYinshGameExport, yinshGameExportFilename } from "./export-game";
import { derivePhase } from "./interaction/derive-phase";
import { phaseCopy, WAITING_COPY } from "./interaction/phase-copy";
import { resolveBoardTap } from "./interaction/resolve-board-tap";
import {
  applyRowPick,
  EMPTY_ROW_PICK,
  type RowPick,
} from "./interaction/resolve-row-run";
import {
  multiplayerGameActions,
  useMultiplayerGameStore,
} from "./multiplayer-game-store";
import type { YinshReplaySnapshot } from "./replay-runner";
import { engineToCanonical } from "./utils/board-geometry";

export function GameScreen() {
  const route = useRoute();
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const {
    gameState,
    myParticipantId,
    selectedRingPosition,
    validDestinations,
  } = useMultiplayerGameStore();
  const actions = multiplayerGameActions;

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const isObserver =
    user?.id !== undefined &&
    (currentRoom?.observerUserIds ?? []).includes(user.id) &&
    !(currentRoom?.participantUserIds ?? []).includes(user.id);

  // Result modal: open by default when the game ends, but the user can
  // close it to inspect the final board and reopen via a FAB.
  const [resultModalOpen, setResultModalOpen] = useState(true);
  const [reviewSnapshots, setReviewSnapshots] = useState<
    readonly YinshReplaySnapshot[] | null
  >(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [rowPick, setRowPick] = useState<RowPick>(EMPTY_ROW_PICK);
  const prevPhaseRef = useRef<GamePhase | null>(null);

  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq) => {
      actionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentRoom is captured once at mount; every game:room_updated event creates a new object reference and would otherwise re-run init() and wipe in-progress game state.
  useEffect(() => {
    if (!roomId) return;
    if (isObserver) {
      void actions.initializeAsObserver(roomId);
    } else if (isHost) {
      const playerIds = currentRoom?.participantUserIds ?? [];
      const playerNames = getParticipantDisplayNames(currentRoom, "Player");
      void actions.initializeAsHost(roomId, playerIds, playerNames);
    } else {
      actions.initializeAsGuest(roomId);
    }
    return () => {
      actions.reset();
    };
  }, [roomId, isHost, isObserver]);

  // Track GAME_END entry so closing-then-reopening the modal works across
  // re-renders. Auto-reopens only on the transition INTO GAME_END.
  // Lives above the early-return to satisfy rules-of-hooks; reads phase
  // defensively since gameState may be null on the first render.
  const phaseForTracking: GamePhase | null = gameState?.phase ?? null;
  useEffect(() => {
    const prev = prevPhaseRef.current;
    if (prev !== "GAME_END" && phaseForTracking === "GAME_END") {
      setResultModalOpen(true);
    }
    prevPhaseRef.current = phaseForTracking;
  }, [phaseForTracking]);

  // Esc cancels a locked ring selection (the canonical cancel, alongside the
  // [취소] pill). Bound only while a ring is selected so it can't swallow Esc
  // elsewhere (e.g. the game-over modal).
  useEffect(() => {
    if (selectedRingPosition === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") multiplayerGameActions.selectRing(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedRingPosition]);

  // Clear the begin/end row pick whenever we're not actively selecting a row,
  // so a stale pick can't leak into the next scoring sequence.
  const inSelectRow =
    gameState?.phase === "REMOVING_ROW" &&
    (gameState?.pendingRows.length ?? 0) > 0;
  useEffect(() => {
    if (!inSelectRow) setRowPick(EMPTY_ROW_PICK);
  }, [inSelectRow]);

  function handleLeave() {
    actions.reset();
    navigateTo(
      buildLobbyPath(
        "yinsh",
        "routeMode" in route && route.routeMode === "v2" ? "v2" : "legacy",
      ),
    );
  }

  function handleForfeit() {
    if (!gameState || gameState.phase === "GAME_END") return;
    const ok = window.confirm("정말 기권하시겠습니까? 상대방이 승리합니다.");
    if (!ok) return;
    void actions.forfeit();
  }

  async function handleReview() {
    if (!roomId) return;
    try {
      const snapshots = await actions.fetchReplaySnapshots(roomId);
      setReviewSnapshots(snapshots);
    } catch (error) {
      window.alert(
        error instanceof Error
          ? error.message
          : "리플레이를 불러올 수 없습니다",
      );
    }
  }

  function handleExport() {
    if (!reviewSnapshots || reviewSnapshots.length === 0) return;
    const finalState = reviewSnapshots[reviewSnapshots.length - 1]?.state;
    if (!finalState) return;
    const exportData = buildYinshGameExport({
      gameId: roomId ?? "yinsh-game",
      capturedAt: new Date().toISOString(),
      events: reviewSnapshots.map((snapshot) => snapshot.event),
      finalState,
    });
    downloadJson(yinshGameExportFilename(exportData), exportData);
  }

  async function handleShare() {
    if (!reviewSnapshots || reviewSnapshots.length === 0) return;
    try {
      const share = await actions.createReplayShare(
        reviewSnapshots.map((snapshot) => snapshot.event),
      );
      const origin =
        typeof window === "undefined" ? "" : window.location.origin;
      const link = `${origin}/replay/room?code=${share.code}`;
      setShareLink(link);
      await navigator.clipboard?.writeText(link).catch(() => undefined);
    } catch (error) {
      window.alert(
        error instanceof Error
          ? error.message
          : "공유 링크 생성에 실패했습니다",
      );
    }
  }

  if (!gameState) {
    return (
      <div
        data-testid="game-loading"
        style={{
          padding: 24,
          textAlign: "center",
          color: "var(--cn-text-secondary)",
        }}
      >
        게임을 불러오는 중…
      </div>
    );
  }

  const { phase, currentPlayer, board, pendingRows, players } = gameState;
  const uiPhase = derivePhase(gameState, selectedRingPosition);
  const isMovingRing = uiPhase === "MOVING_RING";
  const myColor: PlayerColor | null = isHost
    ? "WHITE"
    : myParticipantId && !isObserver
      ? "BLACK"
      : null;

  // Display names come from currentRoom.participants (latest server state),
  // NOT from gameState.players[].name (which is frozen at init time).
  // When initializeAsHost ran before participants were resolved both engine
  // names defaulted to "Player"; reading from the room every render fixes
  // that without re-initializing the game.
  const whiteName = getParticipantDisplayName(
    currentRoom,
    currentRoom?.hostUserId,
    players[0]?.name || "Player 1",
  );
  const blackName = getParticipantDisplayName(
    currentRoom,
    currentRoom?.guestUserId,
    players[1]?.name || "Player 2",
  );

  const isMyTurn =
    myColor !== null &&
    ((myColor === "WHITE" && currentPlayer === "WHITE") ||
      (myColor === "BLACK" && currentPlayer === "BLACK") ||
      myParticipantId === currentPlayer);

  function handlePositionClick(position: string) {
    if (!isMyTurn || !gameState) return;
    const action = resolveBoardTap(
      {
        phase,
        currentPlayer,
        board,
        selectedRingPosition,
        validDestinations,
        pendingRows,
      },
      position,
    );
    switch (action.kind) {
      case "placeRing":
        void actions.placeRing(action.position);
        break;
      case "selectRing":
        actions.selectRing(action.position);
        break;
      case "move":
        void actions.takeTurn(action.from, action.to);
        break;
      case "removeRing":
        void actions.removeRing(action.position);
        break;
      case "rowCandidate":
        handleRowCandidate(action.position);
        break;
      case "ignore":
        break;
    }
  }

  function handleRowCandidate(position: string) {
    const result = applyRowPick(rowPick, pendingRows, position);
    if (result.run) {
      void actions.selectRow(result.run);
      setRowPick(EMPTY_ROW_PICK);
    } else {
      setRowPick(result.pick);
    }
  }

  const currentPlayerName = currentPlayer === "WHITE" ? whiteName : blackName;

  const opponentName =
    myColor === "WHITE" ? blackName : myColor === "BLACK" ? whiteName : "상대";

  const winnerLabel =
    gameState.winner === "WHITE"
      ? "흰색"
      : gameState.winner === "BLACK"
        ? "검은색"
        : null;
  // Rings still to place. Only meaningful (and only shown) during
  // PLACING_RINGS: there `ringsOnBoard` is a monotonic placement counter, so
  // `ringsPerPlayer − ringsOnBoard` is exact. Later, scoring decrements
  // `ringsOnBoard`, which would make the subtraction wrong — hence the guard.
  // players[0] is WHITE, players[1] is BLACK (see init-game.ts).
  const isPlacing = uiPhase === "PLACING_RINGS";
  const copy = phaseCopy(uiPhase, {
    whiteRingsLeft: isPlacing
      ? DEFAULT_CONFIG.ringsPerPlayer - (players[0]?.ringsOnBoard ?? 0)
      : 0,
    blackRingsLeft: isPlacing
      ? DEFAULT_CONFIG.ringsPerPlayer - (players[1]?.ringsOnBoard ?? 0)
      : 0,
    winnerLabel,
  });

  // Right rail hint: when it's the opponent's turn, show the waiting copy.
  const railCopy = phase !== "GAME_END" && !isMyTurn ? WAITING_COPY : copy;

  // Selection-info card content for the right rail.
  const selectionLines: string[] = [];
  if (selectedRingPosition) {
    selectionLines.push(`${engineToCanonical(selectedRingPosition)} 링`);
    selectionLines.push(`이동 가능 ${validDestinations.length}곳`);
  }

  return (
    <div
      data-testid="game-screen"
      style={{
        minHeight: "100vh",
        background: "var(--cn-surface-secondary)",
        color: "var(--cn-text-primary)",
        overflowX: "hidden",
        boxSizing: "border-box",
        // Respect iOS notch / home-indicator insets (no-op until the app sets
        // viewport-fit=cover on the meta viewport).
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
      }}
    >
      <TopBar
        phase={phase}
        currentPlayerName={currentPlayerName}
        connectionStatus={connectionStatus}
        onLeave={handleLeave}
        onForfeit={myColor !== null && !isObserver ? handleForfeit : undefined}
      />

      {isObserver && (
        <div
          data-testid="observer-mode-banner"
          style={{
            padding: "8px 16px",
            background: "var(--cn-accent-blue-bg)",
            color: "var(--cn-accent-blue-text)",
            borderBottom: "1px solid var(--cn-accent-blue-border)",
            textAlign: "center",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          관전 모드 · 읽기 전용
        </div>
      )}

      <TurnBanner
        isMyTurn={isMyTurn}
        isGameOver={phase === "GAME_END"}
        opponentName={opponentName}
        hint={phase !== "GAME_END" && isMyTurn ? copy.hint : undefined}
      />

      <div
        className="yinsh-game-layout"
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 240px) minmax(0, 1fr) minmax(0, 240px)",
          gap: 24,
          padding: 24,
          maxWidth: 1200,
          margin: "0 auto",
          alignItems: "start",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        {/* Left rail — players */}
        <aside>
          <GameInfo
            gameState={gameState}
            myColor={myColor}
            whiteName={whiteName}
            blackName={blackName}
          />
        </aside>

        {/* Center — board frame */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
            width: "100%",
          }}
        >
          <div
            data-testid="board-frame"
            style={{
              position: "relative",
              background: "var(--cn-surface-primary)",
              border: "1px solid var(--cn-border-subtle)",
              borderRadius: "var(--cn-radius-lg)",
              padding: 12,
              boxShadow: "var(--cn-shadow-sm)",
              width: "100%",
              maxWidth: 720,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            {/* At-board phase chip. Visual only — TurnBanner is the single
                aria-live region, so this is aria-hidden to avoid a 2nd one. */}
            <div
              data-testid="phase-chip"
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 10,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1,
                pointerEvents: "none",
                padding: "3px 12px",
                borderRadius: 999,
                background: "var(--cn-yinsh-phase-bg)",
                color: "var(--cn-yinsh-phase-text)",
                border: "1px solid var(--cn-yinsh-phase-border)",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {copy.label}
            </div>
            <Board
              gameState={gameState}
              selectedRingPosition={selectedRingPosition}
              validDestinations={isMyTurn ? validDestinations : []}
              onPositionClick={handlePositionClick}
              disabled={!isMyTurn}
            />
            {isMovingRing && (
              <button
                type="button"
                data-testid="cancel-selection"
                onClick={() => actions.selectRing(null)}
                style={{
                  position: "absolute",
                  bottom: 12,
                  right: 12,
                  minWidth: 44,
                  minHeight: 44,
                  padding: "8px 16px",
                  background: "var(--cn-surface-primary)",
                  color: "var(--cn-text-primary)",
                  border: "1px solid var(--cn-border-default)",
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  boxShadow: "var(--cn-shadow-md)",
                }}
              >
                취소
              </button>
            )}
          </div>
        </div>

        {/* Right rail — hint + selection info */}
        <aside style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {uiPhase === "SELECT_ROW" && isMyTurn && (
            <RowPickerSheet
              begin={rowPick.begin}
              end={rowPick.end}
              onReset={() => setRowPick(EMPTY_ROW_PICK)}
            />
          )}

          <HintCard label={railCopy.label} text={railCopy.hint} />

          {selectionLines.length > 0 && (
            <div
              data-testid="selection-card"
              style={{
                background: "var(--cn-surface-primary)",
                border: "1px solid var(--cn-border-subtle)",
                borderRadius: "var(--cn-radius-lg)",
                padding: 16,
                fontSize: 13,
                color: "var(--cn-text-secondary)",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "var(--cn-text-muted)",
                  marginBottom: 4,
                }}
              >
                선택됨
              </div>
              {selectionLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          )}
        </aside>
      </div>

      {phase === "GAME_END" && (
        <>
          <GameOverModal
            gameState={gameState}
            open={resultModalOpen}
            onClose={() => setResultModalOpen(false)}
            onLeave={handleLeave}
            onReview={() => void handleReview()}
            whiteName={whiteName}
            blackName={blackName}
          />
          {!resultModalOpen && (
            <button
              type="button"
              data-testid="game-over-reopen"
              aria-label="결과 보기"
              onClick={() => setResultModalOpen(true)}
              style={{
                position: "fixed",
                bottom: 16,
                right: 16,
                zIndex: 50,
                padding: "10px 16px",
                background: "var(--cn-accent-blue)",
                color: "white",
                border: "none",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                boxShadow: "var(--cn-shadow-md)",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span aria-hidden="true">🏁</span> 결과
            </button>
          )}
        </>
      )}

      {reviewSnapshots && (
        <div
          data-testid="yinsh-replay-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 200,
            overflowY: "auto",
            background: "var(--cn-surface-primary)",
          }}
        >
          {shareLink && (
            <div
              data-testid="yinsh-replay-share-link"
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                padding: "8px 16px",
                background: "var(--cn-accent-blue-bg)",
                borderBottom: "1px solid var(--cn-accent-blue-border)",
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--cn-accent-blue-text)",
                  whiteSpace: "nowrap",
                }}
              >
                공유 링크 (복사됨)
              </span>
              <input
                readOnly
                value={shareLink}
                data-testid="yinsh-replay-share-link-input"
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: "6px 10px",
                  fontSize: 12,
                  borderRadius: 6,
                  border: "1px solid var(--cn-border-default)",
                  background: "var(--cn-surface-primary)",
                  color: "var(--cn-text-primary)",
                }}
              />
            </div>
          )}
          <ReplayStepper
            snapshots={reviewSnapshots}
            whiteName={whiteName}
            blackName={blackName}
            onExit={() => {
              setReviewSnapshots(null);
              setShareLink(null);
            }}
            onExport={handleExport}
            onShare={() => void handleShare()}
          />
        </div>
      )}

      <style>{`
        @media (max-width: 980px) {
          .yinsh-game-layout {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
            padding: 12px !important;
          }
        }
        @media (max-width: 480px) {
          .yinsh-game-layout {
            padding: 8px !important;
            gap: 8px !important;
          }
        }
      `}</style>
    </div>
  );
}
